import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import {
  ClinicalEventType,
  type InvestigationActivitySyncPushInput,
  type QueryPullInvestigationActivityArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { InvestigationActivity as RxInvestigationActivity } from "../collections/investigation-activity";
import type { RxDBCollections } from "../provider";
import {
  InvestigationActivityPullDocument,
  InvestigationActivityPushDocument,
  type WholeSyncableInvestigationActivityFragment,
} from "./__generated__/investigation-activity.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import { mapGqlInvestigationCatalogueItemToRxInvestigationCatalogueItem } from "./investigation-catalogue-item";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

const CLINICAL_EVENT_TYPE_MAP_RX_TO_GQL: Record<
  RxInvestigationActivity["clinicalEvent"]["type"],
  ClinicalEventType
> = {
  ANC_ENCOUNTER: ClinicalEventType.AncEncounter,
  CONSULTATION: ClinicalEventType.Consultation,
  VISIT: ClinicalEventType.Visit,
};

export const useReplicateInvestigationActivity = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxInvestigationActivity>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;

  const push = useMemo(
    () => ({
      mutation: InvestigationActivityPushDocument,
      mapRxToGql: mapRxInvestigationActivitytoGqlInvestigationActivityInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableInvestigationActivityFragment>
  >(() => {
    return patientId
      ? {
          query: InvestigationActivityPullDocument,
          reSyncInterval: { seconds: 30 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullInvestigationActivityArgs,
            WholeSyncableInvestigationActivityFragment
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: { limit: batchSize, filters: { patientId }, checkpoint },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [patientId, db, collection]);

  return useGqlReplication<
    RxInvestigationActivity,
    WholeSyncableInvestigationActivityFragment,
    InvestigationActivitySyncPushInput
  >({
    db,
    collection,
    push,
    pull,
    mapGqlToRx: mapGqlInvestigationActivityToRxInvestigationActivity,
    batchSize: 25,
    expireData: true,
    metadataLocalStorageKeySuffix: `investigation-activity-${patientId}`,
  });
};

/*
 * Warning: If you're using this outside of the RxDB module, something is amiss.
 * Other areas of the app should hold on concern for how RxDB data maps to GQL,
 * and should only be concerned with the GQL data.
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 *
 * It's more code writing out each field individually, but it's more performant
 * than spreads and stops us from saving fields like __typename onto the RxDB
 * document (RxDB never performs schema validation on what you put in it) which
 * makes conflicts more likely.
 */
export const mapRxInvestigationActivitytoGqlInvestigationActivityInput = (
  investigationActivity: WithDeleted<RxInvestigationActivity>,
): InvestigationActivitySyncPushInput => ({
  id: investigationActivity.id,
  patientId: investigationActivity.patientId,
  catalogueItem: {
    id: investigationActivity.catalogueItem.id,
    title: { en: investigationActivity.catalogueItem.title.en },
    result: investigationActivity.catalogueItem.result ?? null,
    activityType: investigationActivity.catalogueItem.activityType,
    coding: investigationActivity.catalogueItem.coding
      ? {
          elephant: investigationActivity.catalogueItem.coding.elephant ?? null,
          loinc: investigationActivity.catalogueItem.coding.loinc ?? null,
        }
      : null,
    version: investigationActivity.catalogueItem.version,
    keywords: investigationActivity.catalogueItem.keywords ?? null,
  },
  clinicalEvent: investigationActivity.clinicalEvent
    ? {
        id: investigationActivity.clinicalEvent.id ?? null,
        type: investigationActivity.clinicalEvent.type
          ? CLINICAL_EVENT_TYPE_MAP_RX_TO_GQL[
              investigationActivity.clinicalEvent.type
            ]
          : null,
      }
    : null,
  meta: {
    createdAt: investigationActivity.meta.createdAt,
    createdByUserId: investigationActivity.meta.createdByUserId,
    updatedAt: investigationActivity.meta.updatedAt,
    updatedByUserId: investigationActivity.meta.updatedByUserId,
    deleted: false,
  },
  outcome: investigationActivity.outcome
    ? {
        clinic: {
          id: investigationActivity.outcome.clinic.id,
          name: investigationActivity.outcome.clinic.name,
        },
        orderedProperties: investigationActivity.outcome.orderedProperties,
        organisation: {
          id: investigationActivity.outcome.organisation.id,
          name: investigationActivity.outcome.organisation.name,
        },
        performer: {
          id: investigationActivity.outcome.performer.id,
          name: investigationActivity.outcome.performer.name,
        },
        meta: {
          created: investigationActivity.outcome.meta.created,
          updated: investigationActivity.outcome.meta.updated,
        },
      }
    : null,
  request: investigationActivity.request
    ? {
        clinic: {
          id: investigationActivity.request.clinic.id,
          name: investigationActivity.request.clinic.name,
        },
        orderedProperties: investigationActivity.request.orderedProperties,
        organisation: {
          id: investigationActivity.request.organisation.id,
          name: investigationActivity.request.organisation.name,
        },
        performer: {
          id: investigationActivity.request.performer.id,
          name: investigationActivity.request.performer.name,
        },
        meta: {
          created: investigationActivity.request.meta.created,
          updated: investigationActivity.request.meta.updated,
        },
      }
    : null,
});
/*
 * Warning: If you're using this outside of the RxDB module, something is amiss.
 * Other areas of the app should hold on concern for how RxDB data maps to GQL,
 * and should only be concerned with the GQL data.
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 *
 * It's more code writing out each field individually, but it's more performant
 * than spreads and stops us from saving fields like __typename onto the RxDB
 * document (RxDB never performs schema validation on what you put in it) which
 * makes conflicts more likely.
 */
export const mapGqlInvestigationActivityToRxInvestigationActivity = (
  investigationActivity: WholeSyncableInvestigationActivityFragment,
): WithDeleted<RxInvestigationActivity> => ({
  id: investigationActivity.id,
  // TODO: try to remove the spread of investigationActivity.catalogueItem into this arg
  catalogueItem: mapGqlInvestigationCatalogueItemToRxInvestigationCatalogueItem(
    {
      ...investigationActivity.catalogueItem,
      meta:
        investigationActivity.catalogueItem.meta ?? investigationActivity.meta,
    },
  ),
  clinicalEvent: investigationActivity.clinicalEvent ?? null,
  meta: {
    createdAt: investigationActivity.meta.createdAt,
    createdByUserId: investigationActivity.meta.createdByUserId,
    updatedAt: investigationActivity.meta.updatedAt,
    updatedByUserId: investigationActivity.meta.updatedByUserId,
  },
  outcome: investigationActivity.outcome
    ? {
        id: investigationActivity.id,
        clinic: {
          id: investigationActivity.outcome.clinic.id,
          name: investigationActivity.outcome.clinic.name,
        },
        organisation: {
          id: investigationActivity.outcome.organisation.id,
          name: investigationActivity.outcome.organisation.name,
        },
        performer: {
          id: investigationActivity.outcome.performer.id,
          name: investigationActivity.outcome.performer.name,
        },
        orderedProperties: [...investigationActivity.outcome.orderedProperties],
        meta: investigationActivity.outcome.meta,
      }
    : null,
  patientId: investigationActivity.patientId,
  request: investigationActivity.request
    ? {
        id: investigationActivity.id,
        clinic: {
          id: investigationActivity.request.clinic.id,
          name: investigationActivity.request.clinic.name,
        },
        organisation: {
          id: investigationActivity.request.organisation.id,
          name: investigationActivity.request.organisation.name,
        },
        performer: {
          id: investigationActivity.request.performer.id,
          name: investigationActivity.request.performer.name,
        },
        orderedProperties: [...investigationActivity.request.orderedProperties],
        meta: {
          ...investigationActivity.request.meta,
          updated:
            investigationActivity.request.meta.updated ??
            investigationActivity.meta.updatedAt,
        },
      }
    : null,
  _deleted: investigationActivity.meta.deleted,
});

gql`
  fragment WholeSyncableInvestigationActivity on SyncableInvestigationActivity {
    id
    patientId
    catalogueItem {
      meta {
        deleted
        updatedAt
        createdAt
        updatedByUserId
        createdByUserId
      }
      activityType
      coding {
        elephant {
          code
          display
          system
        }
        loinc {
          code
          display
          system
        }
      }
      version
      id
      keywords {
        en
      }
      result {
        resultSchema
      }
      title {
        en
      }
    }
    clinicalEvent {
      id
      type
    }
    meta {
      deleted
      updatedAt
      createdAt
      updatedByUserId
      createdByUserId
    }
    outcome {
      clinic {
        id
        name
      }
      meta {
        created
        updated
      }
      orderedProperties
      organisation {
        id
        name
      }
      performer {
        id
        name
      }
    }
    request {
      clinic {
        id
        name
      }
      meta {
        created
        updated
      }
      orderedProperties
      organisation {
        id
        name
      }
      performer {
        id
        name
      }
    }
  }
   `;

gql`
  mutation InvestigationActivityPush($input:PushInvestigationActivityInput!) {
    pushInvestigationActivity(input: $input) {
      rows {
        ...WholeSyncableInvestigationActivity
      }
    }
  }
`;

gql`
  query InvestigationActivityPull($input:PullInvestigationActivityInput!) {
    pullInvestigationActivity(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableInvestigationActivity
      }
    }
  }
`;
